import { Button, Flex, Select, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  describeCreatedBy,
  editionLabel,
  editionStatusLabel,
  formatEditionDate
} from "./education-track-helpers.ts"
import type { EditionView } from "./types/education.types.ts"

const { Text } = Typography

interface EditionHeaderProps {
  editions: EditionView[]
  selectedEdition: EditionView
  /** The signed-in account's own date format (e.g. "DD/MM/YYYY") — resolved
   *  by the caller via `useDateFormat()` so this component stays free of
   *  Redux and testable with a plain string. */
  dateFormat: string
  onSelectEdition: (editionId: string) => void
  onOpenHistory: () => void
  children?: React.ReactNode
}

/**
 * Screen 2's second header row: the edition selector (label + status + date,
 * never just a bare number — a reader must be able to tell Base from Section 2
 * without opening either), who generated it, a link into
 * the full edition history (Screen 4), and the audio player slot.
 */
export function EditionHeader({
  editions,
  selectedEdition,
  dateFormat,
  onSelectEdition,
  onOpenHistory,
  children
}: EditionHeaderProps) {
  const { t } = useTranslation()
  const separator = t("projectEducation.trackView.editionOptionSeparator")

  const options = editions.map((edition) => ({
    value: edition.id,
    label: [
      editionLabel(edition, t),
      editionStatusLabel(edition, t),
      formatEditionDate(edition.created_at, dateFormat)
    ].join(separator)
  }))

  return (
    <Flex vertical gap={"small"} data-testid={"education-edition-header"}>
      <Flex wrap align={"center"} gap={"middle"}>
        <Flex align={"center"} gap={8}>
          <Text type={"secondary"}>{t("projectEducation.trackView.editionLabel")}</Text>
          <Select
            data-testid={"edition-select"}
            aria-label={t("projectEducation.trackView.editionSelectLabel")}
            value={selectedEdition.id}
            options={options}
            style={{ minWidth: 260 }}
            onChange={(value: string) => onSelectEdition(value)}
          />
        </Flex>

        <Text type={"secondary"}>{describeCreatedBy(selectedEdition.created_by, t)}</Text>

        <Button type={"link"} onClick={onOpenHistory}>
          {t("projectEducation.trackView.history")}
        </Button>
      </Flex>

      {children}
    </Flex>
  )
}
